import axios, {AxiosError, AxiosResponse} from "axios";
import type {
    CreateRouteRequestParams,
    CreateRouteSuccessResponse,
    CreateWorkspaceRequestParams,
    CreateWorkspaceSuccessResponse,
    DeleteRouteRequestParams,
    DeleteRouteSuccessResponse,
    DeleteWorkspaceRequestParams,
    DeleteWorkspaceSuccessResponse,
    GetOneWorkspaceSuccessResponse,
    GetRouteLogsRequestParams,
    GetLogRequestParams,
    GetRouteRequestParams,
    GetRouteSuccessResponse,
    GetWorkspaceRequestParams,
    GetWorkspaceRoutesRequestParams,
    GetWorkspacesSuccessResponse,
    InfoSuccessResponse,
    LoginFormResponseSuccess,
    LoginFormValues,
    RecoveryPasswordRequestParams,
    RecoveryPasswordSuccessResponse,
    RegistrationErrorResponse,
    GetRouteLogsSuccessResponse,
    RegistrationRequestParams,
    RegistrationSuccessResponse,
    SupportRequestParams,
    UpdateRouteRequestParams,
    UpdateWorkspaceRequestParams,
    UpdateWorkspaceSuccessResponse,
    UploadFileRequestParams,
    UploadFileSuccessResponse,
    LogType,
} from "../types";
import {API_ROUTES} from "../constants";
import {
    SettingsUpdateRequestParams,
    SettingsUpdateSuccessResponse,
} from "../types/settings/settings.types";
import {
    ApiTokenCreateResponse,
    ApiTokenListResponse,
} from "../types/settings/tokens.types";
import {ChangePasswordRequestParams} from "@src/core/types/password-change";

export const Authentication = {
    login: async (
        data: LoginFormValues,
    ): Promise<AxiosResponse<LoginFormResponseSuccess, unknown>> => {
        return axios({
            method: "post",
            url: API_ROUTES.LOGIN,
            headers: {
                "Content-Type": "multipart/form-data",
            },
            data,
        });
    },
    registration: async (
        data: RegistrationRequestParams,
    ): Promise<AxiosResponse<RegistrationSuccessResponse, unknown>> => {
        try {
            return axios({
                method: "post",
                url: API_ROUTES.REGISTRATION,
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                data,
            });
        } catch (error: AxiosError<RegistrationErrorResponse> | any) {
            // Обработка ошибки
            console.log(error.response?.data); // Доступ к данным ошибки

            throw error;
        }
    },
    info: async (): Promise<AxiosResponse<InfoSuccessResponse, unknown>> => {
        return axios({
            method: "get",
            url: API_ROUTES.INFO,
            withCredentials: true,
        });
    },
    statistics: async (): Promise<AxiosResponse<{workspaces_count: number, routes_count: number, requests_count: number}, unknown>> => {
        return axios({
            method: "get",
            url: API_ROUTES.STATISTICS,
        });
    },
    nps: async (data: {stars: number, value?: string}): Promise<AxiosResponse<{result: string, message: string}, unknown>> => {
        return axios({
            method: "post",
            url: API_ROUTES.NPS,
            data,
        });
    },
    logout: async (): Promise<AxiosResponse<RegistrationSuccessResponse, unknown>> => {
        return axios({
            method: "get",
            url: API_ROUTES.LOGOUT,
        });
    },
    recoveryPassword: async (
        data: RecoveryPasswordRequestParams,
    ): Promise<AxiosResponse<RecoveryPasswordSuccessResponse, unknown>> => {
        return axios({
            method: "post",
            url: API_ROUTES.RECOVERY_PASSWORD,
            data,
        });
    },
};

export const Workspace = {
    getAll: async (): Promise<AxiosResponse<GetWorkspacesSuccessResponse, unknown>> => {
        return axios({
            method: "get",
            url: API_ROUTES.WORKSPACE,
            withCredentials: true,
        });
    },
    getOne: async (
        data: GetWorkspaceRequestParams,
    ): Promise<AxiosResponse<GetOneWorkspaceSuccessResponse, unknown>> => {
        return await axios({
            method: "get",
            url: `${API_ROUTES.WORKSPACE}/${data.workspaceId}`,
            withCredentials: true,
        });
    },
    create: async (
        data: CreateWorkspaceRequestParams,
    ): Promise<AxiosResponse<CreateWorkspaceSuccessResponse, unknown>> => {
        return axios({
            method: "post",
            url: API_ROUTES.WORKSPACE,
            data,
            withCredentials: true,
        });
    },
    update: async (
        data: UpdateWorkspaceRequestParams,
    ): Promise<AxiosResponse<UpdateWorkspaceSuccessResponse, unknown>> => {
        return axios({
            method: "put",
            url: `${API_ROUTES.WORKSPACE}/${data.workspaceId}`,
            data,
            withCredentials: true,
        });
    },
    delete: async (
        data: DeleteWorkspaceRequestParams,
    ): Promise<AxiosResponse<DeleteWorkspaceSuccessResponse, unknown>> => {
        return axios({
            method: "delete",
            url: `${API_ROUTES.WORKSPACE}/${data.workspaceId}/delete`,
            withCredentials: true,
        });
    },
    uploadFile: async (
        data: FormData,
    ): Promise<AxiosResponse<UploadFileSuccessResponse, unknown>> => {
        return axios({
            method: "post",
            url: `${API_ROUTES.UPLOAD}`,
            data,
        });
    },
};

export const Route = {
    getAll: async (data: GetWorkspaceRoutesRequestParams) => {
        return axios({
            method: "get",
            url: `${API_ROUTES.ROUTES}/${data.workspaceId}/view`,
            withCredentials: true,
        });
    },
    getOne: async (
        data: GetRouteRequestParams,
    ): Promise<AxiosResponse<GetRouteSuccessResponse, unknown>> => {
        return axios({
            method: "get",
            url: `${API_ROUTES.ROUTES}/${data.routeId}/view`,
            withCredentials: true,
        });
    },
    create: async (
        data: CreateRouteRequestParams,
    ): Promise<AxiosResponse<CreateRouteSuccessResponse, unknown>> => {
        return axios({
            method: "post",
            url: `${API_ROUTES.ROUTES}/add`,
            data,
            withCredentials: true,
        });
    },
    update: async (
        data: UpdateRouteRequestParams,
    ): Promise<AxiosResponse<CreateRouteSuccessResponse, unknown>> => {
        return axios({
            method: "put",
            url: `${API_ROUTES.ROUTES}/${data.route_id}/update`,
            data: data.body,
            withCredentials: true,
        });
    },
    delete: async (
        data: DeleteRouteRequestParams,
    ): Promise<AxiosResponse<DeleteRouteSuccessResponse, unknown>> => {
        return axios({
            method: "delete",
            url: `${API_ROUTES.ROUTES}/${data.route_id}/delete`,
            withCredentials: true,
        });
    },
};

export const Logs = {
    getOne: async (
        data: GetLogRequestParams,
    ): Promise<AxiosResponse<LogType, unknown>> => {
        return axios({
            method: "get",
            url: `${API_ROUTES.LOG}/${data.logId}`,
            withCredentials: true,
        });
    },
    getAll: async (
        data: GetRouteLogsRequestParams,
    ): Promise<AxiosResponse<GetRouteLogsSuccessResponse, unknown>> => {
        return axios({
            method: "get",
            url: `${API_ROUTES.LOG}s`,
            params: {
                route_id: data.routeId,
                limit: data.limit,
                offset: data.offset,
                date_start: data.date_start,
                date_finish: data.date_finish,
            },
            withCredentials: true,
        });
    },
};

export const Settings = {
    update: async (
        data: SettingsUpdateRequestParams,
    ): Promise<AxiosResponse<SettingsUpdateSuccessResponse, unknown>> => {
        return axios({
            method: "post",
            url: `${API_ROUTES.SETTINGS}`,
            data,
            withCredentials: true,
        });
    },
};

export const ApiTokens = {
    list: async (): Promise<AxiosResponse<ApiTokenListResponse, unknown>> => {
        return axios({
            method: "get",
            url: API_ROUTES.TOKENS,
            withCredentials: true,
        });
    },
    create: async (
        name: string,
    ): Promise<AxiosResponse<ApiTokenCreateResponse, unknown>> => {
        return axios({
            method: "post",
            url: API_ROUTES.TOKENS,
            data: {name},
            withCredentials: true,
        });
    },
    revoke: async (
        tokenId: number,
    ): Promise<AxiosResponse<{result: string}, unknown>> => {
        return axios({
            method: "delete",
            url: `${API_ROUTES.TOKENS}/${tokenId}`,
            withCredentials: true,
        });
    },
};

export const Support = {
    offer: async (
        data: SupportRequestParams,
    ): Promise<AxiosResponse<unknown, unknown>> => {
        return axios({
            method: "post",
            url: `${API_ROUTES.SUPPORT}`,
            data: {...data, type: "offer"},
        });
    },
    help: async (
        data: SupportRequestParams,
    ): Promise<AxiosResponse<unknown, unknown>> => {
        return axios({
            method: "post",
            url: `${API_ROUTES.SUPPORT}`,
            data: {...data, type: "help"},
        });
    },
    password_change: async (
        data: ChangePasswordRequestParams,
    ): Promise<AxiosResponse<unknown, unknown>> => {
        return axios({
            method: "post",
            url: `${API_ROUTES.CHANGE_PASSWORD}`,
            data
        });
    }
};
